import React, { useCallback, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, MaximizeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import type { PropertyImage } from '../data/images';
interface LightboxProps {
  images: PropertyImage[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}
export function Lightbox({
  images,
  index,
  onClose,
  onIndexChange
}: LightboxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const {
    lang,
    t
  } = useLanguage();
  const image = images[index];
  const goNext = useCallback(() => {
    onIndexChange((index + 1) % images.length);
  }, [index, images.length, onIndexChange]);
  const goPrev = useCallback(() => {
    onIndexChange((index - 1 + images.length) % images.length);
  }, [index, images.length, onIndexChange]);
  useEffect(() => {
    dialogRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') goNext();
      if (event.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, onClose]);
  const requestFullScreen = () => {
    const node = dialogRef.current;
    if (!node) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void node.requestFullscreen?.();
    }
  };
  if (!image) return null;
  return <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={t('Property photo gallery', 'Galería de fotos de la propiedad')} tabIndex={-1} className="fixed inset-0 z-[70] flex flex-col bg-midnight/97" onTouchStart={event => {
    touchStartX.current = event.touches[0].clientX;
  }} onTouchEnd={event => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 48) {
      if (delta < 0) goNext();else goPrev();
    }
    touchStartX.current = null;
  }} data-id="element-351" data-mp="292s39ppbe1aa">
      <div className="flex items-center justify-between px-5 py-4 lg:px-8" data-id="element-352" data-mp="gbwpvhpv7hds">
        <p className="label-caps text-white/70" data-id="element-353" data-mp="l2mz561ew4dr">
          {index + 1} / {images.length}
        </p>
        <div className="flex items-center gap-2" data-id="element-354" data-mp="6eabcwch9ruc">
          <button type="button" onClick={requestFullScreen} aria-label={t('Toggle full screen', 'Pantalla completa')} className="p-2 text-white/80 transition-colors duration-150 ease-out hover:text-gold" data-id="element-355" data-mp="6flbmm6pshgp">
            <MaximizeIcon className="h-5 w-5" aria-hidden="true" data-id="element-356" data-mp="2cos8t9cohbp" />
          </button>
          <button type="button" onClick={onClose} aria-label={t('Close gallery', 'Cerrar galería')} className="p-2 text-white/80 transition-colors duration-150 ease-out hover:text-gold" data-id="element-357" data-mp="3evholr0ssngm">
            <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-358" data-mp="3ny90frxl5l8s" />
          </button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-3 pb-4 lg:px-16" data-id="element-359" data-mp="3jtsxnzj9qtxs">
        <button type="button" onClick={goPrev} aria-label={t('Previous photo', 'Foto anterior')} className="absolute left-2 z-10 border border-white/25 bg-midnight/60 p-3 text-white transition-colors duration-150 ease-out hover:border-gold hover:text-gold lg:left-6" data-id="element-360" data-mp="1nn227zw6hhz">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-361" data-mp="6ik7z61vkaqn" />
        </button>

        <img key={image.id} src={image.src} alt={lang === 'en' ? image.altEn : image.altEs} width={image.width} height={image.height} className="max-h-full max-w-full object-contain" data-id="element-362" data-mp="kdwcg55u1fkr" />

        <button type="button" onClick={goNext} aria-label={t('Next photo', 'Foto siguiente')} className="absolute right-2 z-10 border border-white/25 bg-midnight/60 p-3 text-white transition-colors duration-150 ease-out hover:border-gold hover:text-gold lg:right-6" data-id="element-363" data-mp="2eve68d6ogm5v">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-364" data-mp="1qzjwppfte5dw" />
        </button>
      </div>

      <p className="px-5 pb-6 text-center text-[13px] text-white/70 lg:px-8" data-id="element-365" data-mp="1vy4n6x59lap5">
        {lang === 'en' ? image.altEn : image.altEs}
      </p>
    </div>;
}